import React, { useState } from 'react'
import { Dialog } from '@headlessui/react'
import { useTranslation } from 'react-i18next'
import Switch from 'components/atoms/Switch'
import Button from 'components/atoms/Button'
import { find, some, split } from 'lodash'
import { IoClose } from 'react-icons/io5'

const NormalModeDialog = ({ isOpen, onClose, onSave, timings, onTimeChange, values, setFieldValue }) => {
  const { t } = useTranslation()
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  const handleRequestClose = () => {
    if (hasUnsavedChanges) return
    onClose()
  }

  const handleSave = () => {
    const timetable = Object.entries(timings).map(([day, value]) => ({
      day,
      active: value.enabled,
      startTime: value.startTime + ':00',
      endTime: value.endTime + ':00',
    }))

    const existingNormalMode = find(values?.timings, { mode: 'normal' }) || {}

    onSave([
      {
        mode: 'normal',
        isActive: existingNormalMode.isActive ?? false,
        timetable,
      },
    ])

    onClose()
  }

  return (
    <Dialog open={isOpen} onClose={handleRequestClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-2 sm:p-4">
        <Dialog.Panel className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-sm sm:max-w-md md:max-w-xl max-h-[90vh] sm:max-h-[80vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4 sm:mb-6">
            <Dialog.Title className="text-lg sm:text-xl font-semibold">{t('normalModeDialog.title')}</Dialog.Title>
            <button
              onClick={handleRequestClose}
              disabled={hasUnsavedChanges}
              className={`p-1 rounded-full transition-colors ${hasUnsavedChanges ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-100'}`}
            >
              <IoClose className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>

          <div className="mb-4 sm:mb-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-base sm:text-lg font-medium">{t('normalModeDialog.normalWorkingMode')}</h3>
              <Switch
                value={some(values.timings, { mode: 'normal', isActive: true })}
                onChange={e => {
                  setHasUnsavedChanges(true)
                  const existingNormalMode = find(values?.timings, { mode: 'normal' }) || {}
                  setFieldValue('timings', [...(values.timings || []).filter(t => t.mode !== 'normal'), { ...existingNormalMode, mode: 'normal', isActive: e }])
                }}
              />
            </div>
            <p className="text-gray-600 text-xs sm:text-sm">{t('normalModeDialog.description')}</p>
          </div>

          <div className="space-y-3 sm:space-y-4">
            {Object.entries(timings).map(([day, value]) => (
              <div key={day} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4 p-2 sm:p-0">
                <span className="text-gray-700 text-sm sm:text-base font-medium min-w-[80px] sm:min-w-[100px]">{t(`normalModeDialog.days.${day}`)}</span>
                <div className="flex items-center gap-2 sm:gap-4 w-full sm:w-auto">
                  <Switch
                    value={value.enabled}
                    onChange={checked => {
                      setHasUnsavedChanges(true)
                      onTimeChange(day, 'enabled', checked)
                    }}
                    className="flex-shrink-0"
                  />

                  <div className="flex items-center gap-1 sm:gap-2 flex-1 sm:flex-initial">
                    <input
                      type="time"
                      value={split(value.startTime, ' ')[0]}
                      onChange={e => {
                        setHasUnsavedChanges(true)
                        onTimeChange(day, 'startTime', e.target.value)
                      }}
                      className="border border-gray-300 rounded px-2 py-1 text-xs sm:text-sm w-full min-w-[80px] sm:min-w-[90px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={!value.enabled}
                    />
                    <span className="text-gray-500 text-sm">-</span>
                    <input
                      type="time"
                      value={split(value.endTime, ' ')[0]}
                      onChange={e => {
                        setHasUnsavedChanges(true)
                        onTimeChange(day, 'endTime', e.target.value)
                      }}
                      className="border border-gray-300 rounded px-2 py-1 text-xs sm:text-sm w-full min-w-[80px] sm:min-w-[90px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={!value.enabled}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 flex justify-end gap-3">
            <Button onClick={handleRequestClose} disabled={hasUnsavedChanges} extraClassName="bg-gray-500 hover:bg-gray-600">
              {t('normalModeDialog.buttons.cancel')}
            </Button>
            <Button onClick={handleSave}> {t('normalModeDialog.buttons.saveChanges')}</Button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  )
}

export default NormalModeDialog
